export const ANONYMOUS_ONBOARDING_DRAFT_KEY = "lz_onboarding_draft";
export const ANONYMOUS_ONBOARDING_DRAFT_SCHEMA_VERSION = 2;
export const ANONYMOUS_ONBOARDING_DRAFT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const LEGACY_DRAFT_KEY_PREFIX = "lyft_zone_onboarding_draft_v1";
const GENERATION_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
const TRAINING_DAYS = new Set([
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
]);
const TOP_LEVEL_KEYS = Object.freeze([
  "schemaVersion",
  "createdAt",
  "updatedAt",
  "expiresAt",
  "lastCompletedStep",
  "answers",
  "identity",
  "pendingGeneration",
]);

const ANSWER_SHAPE = Object.freeze({
  primaryGoal: "scalar",
  experience: "scalar",
  musclePriorities: {
    primaryFocus: "scalar",
    secondaryFocuses: "array",
    deprioritizedArea: "scalar",
  },
  availability: {
    sessionsPerWeek: "scalar",
    durationPerSession: "scalar",
    preferredTrainingDays: "array",
  },
  environment: {
    equipmentPreset: "scalar",
    availableEquipment: "array",
  },
  movementConstraints: {
    painIssues: [{
      id: "scalar",
      description: "scalar",
      affectedArea: "scalar",
      painSeverity: "scalar",
      trainingRule: "scalar",
      analysisStatus: "scalar",
      clarificationQuestions: [{ id: "scalar", question: "scalar" }],
      clarificationAnswers: [{ questionId: "scalar", answer: "scalar" }],
      aiSummary: "scalar",
      detectedSignals: [{
        type: "scalar",
        value: "scalar",
        recommendedDecision: "scalar",
        cautionLevel: "scalar",
        confidence: "scalar",
        reason: "scalar",
      }],
      confirmedSignals: [{
        type: "scalar",
        value: "scalar",
        decision: "scalar",
        cautionLevel: "scalar",
      }],
    }],
    manualBlockedExerciseIds: "array",
  },
  exercisePreference: { equipmentBias: "scalar" },
  cardioProfile: {
    cardioRole: "scalar",
    preferredModalities: "array",
  },
  // Disabled field: still accepted so a draft saved before the shutdown stays loadable; it is
  // dropped on load and never sent (loadAnonymousOnboardingDraft, toAnonymousDraftAdoptionPayload).
  physicalNotes: "scalar",
});

const IDENTITY_SHAPE = Object.freeze({
  displayName: "scalar",
  age: "scalar",
  sex: "scalar",
});

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value, expectedKeys) {
  if (!isPlainObject(value)) {
    return false;
  }
  const actualKeys = Object.keys(value).sort();
  return actualKeys.length === expectedKeys.length &&
    actualKeys.every((key, index) => key === [...expectedKeys].sort()[index]);
}

function isScalar(value) {
  return value == null || ["string", "number", "boolean"].includes(typeof value);
}

function matchesShape(value, shape) {
  if (!isPlainObject(value)) {
    return false;
  }

  return Object.entries(value).every(([key, childValue]) => {
    if (!Object.prototype.hasOwnProperty.call(shape, key)) {
      return false;
    }
    const childShape = shape[key];
    if (childShape === "scalar") {
      return isScalar(childValue);
    }
    if (childShape === "array") {
      return Array.isArray(childValue) && childValue.every(isScalar);
    }
    if (Array.isArray(childShape)) {
      return Array.isArray(childValue) &&
        childValue.every((entry) => matchesShape(entry, childShape[0]));
    }
    return matchesShape(childValue, childShape);
  });
}

function keyWords(key) {
  return String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

function containsAuthSecretKey(value) {
  if (!value || typeof value !== "object") {
    return false;
  }

  return Object.entries(value).some(([key, child]) => {
    const words = keyWords(key);
    const forbidden = words.some((word) => [
      "token",
      "otp",
      "oauth",
      "pkce",
      "csrf",
      "cookie",
      "password",
      "authorization",
      "secret",
      "verifier",
    ].includes(word)) ||
      (words.includes("session") &&
        words.some((word) => ["id", "credential", "material"].includes(word)));
    return forbidden || containsAuthSecretKey(child);
  });
}

function isIsoTimestamp(value) {
  if (typeof value !== "string") {
    return false;
  }
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && new Date(timestamp).toISOString() === value;
}

function isValidIdentity(identity) {
  return matchesShape(identity, IDENTITY_SHAPE) &&
    (identity.displayName == null || typeof identity.displayName === "string") &&
    (identity.age == null || Number.isFinite(Number(identity.age))) &&
    (identity.sex == null || typeof identity.sex === "string");
}

function isValidPendingGeneration(pendingGeneration) {
  if (pendingGeneration == null) {
    return true;
  }
  if (!hasExactKeys(pendingGeneration, ["generationId", "startedAt", "trainingDays"])) {
    return false;
  }
  return GENERATION_ID_PATTERN.test(String(pendingGeneration.generationId || "")) &&
    isIsoTimestamp(pendingGeneration.startedAt) &&
    Array.isArray(pendingGeneration.trainingDays) &&
    pendingGeneration.trainingDays.length > 0 &&
    pendingGeneration.trainingDays.length <= 7 &&
    new Set(pendingGeneration.trainingDays).size === pendingGeneration.trainingDays.length &&
    pendingGeneration.trainingDays.every((day) => TRAINING_DAYS.has(day));
}

export function deriveAnonymousCurrentStep(lastCompletedStep) {
  const completed = Number(lastCompletedStep);
  return Number.isInteger(completed) && completed >= 0 && completed <= 5
    ? Math.min(5, completed + 1)
    : 1;
}

export function isValidAnonymousOnboardingDraft(record) {
  if (!hasExactKeys(record, TOP_LEVEL_KEYS) || containsAuthSecretKey(record)) {
    return false;
  }

  const createdAt = Date.parse(record.createdAt);
  const updatedAt = Date.parse(record.updatedAt);
  const expiresAt = Date.parse(record.expiresAt);
  const validLifetime = [record.createdAt, record.updatedAt, record.expiresAt].every(isIsoTimestamp) &&
    createdAt <= updatedAt &&
    updatedAt < expiresAt &&
    expiresAt - createdAt === ANONYMOUS_ONBOARDING_DRAFT_TTL_MS;

  return record.schemaVersion === ANONYMOUS_ONBOARDING_DRAFT_SCHEMA_VERSION &&
    validLifetime &&
    Number.isInteger(record.lastCompletedStep) &&
    record.lastCompletedStep >= 0 &&
    record.lastCompletedStep <= 5 &&
    matchesShape(record.answers, ANSWER_SHAPE) &&
    isValidIdentity(record.identity) &&
    isValidPendingGeneration(record.pendingGeneration) &&
    (record.pendingGeneration == null || record.lastCompletedStep === 5);
}

export function createAnonymousOnboardingDraft({
  answers = {},
  identity = {},
  lastCompletedStep = 0,
  pendingGeneration = null,
} = {}, now = Date.now()) {
  const createdAt = new Date(now).toISOString();
  return {
    schemaVersion: ANONYMOUS_ONBOARDING_DRAFT_SCHEMA_VERSION,
    createdAt,
    updatedAt: createdAt,
    expiresAt: new Date(now + ANONYMOUS_ONBOARDING_DRAFT_TTL_MS).toISOString(),
    lastCompletedStep,
    answers,
    identity,
    pendingGeneration,
  };
}

export function updateAnonymousOnboardingDraft(
  draft,
  { answers, identity, completedStep, pendingGeneration } = {},
  now = Date.now()
) {
  const nextLastCompletedStep = completedStep == null
    ? draft.lastCompletedStep
    : Math.max(draft.lastCompletedStep, completedStep);
  return {
    ...draft,
    updatedAt: new Date(now).toISOString(),
    lastCompletedStep: nextLastCompletedStep,
    answers: answers ?? draft.answers,
    identity: identity ?? draft.identity,
    pendingGeneration: pendingGeneration === undefined
      ? draft.pendingGeneration
      : pendingGeneration,
  };
}

export function purgeLegacyOnboardingDrafts(storage = window.localStorage) {
  if (!storage) {
    return;
  }
  const keys = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key?.startsWith(LEGACY_DRAFT_KEY_PREFIX)) {
      keys.push(key);
    }
  }
  keys.forEach((key) => storage.removeItem(key));
}

export function clearAnonymousOnboardingDraft(storage = window.localStorage) {
  storage?.removeItem(ANONYMOUS_ONBOARDING_DRAFT_KEY);
}

export function loadAnonymousOnboardingDraft(
  storage = window.localStorage,
  now = Date.now()
) {
  if (!storage) {
    return null;
  }
  purgeLegacyOnboardingDrafts(storage);

  try {
    const rawValue = storage.getItem(ANONYMOUS_ONBOARDING_DRAFT_KEY);
    if (!rawValue) {
      return null;
    }
    const parsed = JSON.parse(rawValue);
    if (!isValidAnonymousOnboardingDraft(parsed) || Date.parse(parsed.expiresAt) <= now) {
      clearAnonymousOnboardingDraft(storage);
      return null;
    }
    if (parsed.answers && "physicalNotes" in parsed.answers) {
      const { physicalNotes: _disabled, ...answers } = parsed.answers;
      return { ...parsed, answers };
    }
    return parsed;
  } catch {
    clearAnonymousOnboardingDraft(storage);
    return null;
  }
}

export function saveAnonymousOnboardingDraft(
  draft,
  storage = window.localStorage
) {
  if (!storage || !isValidAnonymousOnboardingDraft(draft)) {
    return false;
  }
  storage.setItem(ANONYMOUS_ONBOARDING_DRAFT_KEY, JSON.stringify(draft));
  return true;
}

export function commitAnonymousOnboardingDraftMutation(
  callerDraft,
  mutation = {},
  now = Date.now(),
  storage = window.localStorage
) {
  if (!storage || !isValidAnonymousOnboardingDraft(callerDraft)) {
    return null;
  }

  const candidate = updateAnonymousOnboardingDraft(callerDraft, mutation, now);
  if (!isValidAnonymousOnboardingDraft(candidate)) {
    return null;
  }

  const storedDraft = loadAnonymousOnboardingDraft(storage, now);
  let committedDraft = candidate;
  if (storedDraft) {
    const storedIsMoreAdvanced =
      storedDraft.lastCompletedStep > candidate.lastCompletedStep;
    const storedHasNewerSeal = storedDraft.pendingGeneration && (
      storedDraft.pendingGeneration.generationId !==
        candidate.pendingGeneration?.generationId ||
      Date.parse(storedDraft.updatedAt) > Date.parse(callerDraft.updatedAt)
    );

    if (storedIsMoreAdvanced || storedHasNewerSeal) {
      committedDraft = storedDraft;
    }
  }

  return saveAnonymousOnboardingDraft(committedDraft, storage)
    ? committedDraft
    : null;
}

export function toAnonymousDraftAdoptionPayload(draft) {
  if (!isValidAnonymousOnboardingDraft(draft)) {
    return null;
  }
  const { physicalNotes: _disabled, ...answers } = draft.answers || {};
  return {
    schemaVersion: draft.schemaVersion,
    lastCompletedStep: draft.lastCompletedStep,
    answers,
    identity: draft.identity,
    pendingGeneration: draft.pendingGeneration,
  };
}
